import { FC, MouseEvent } from 'react';

import { CtaLink, CtaLinkProps } from '@purpur/library';
import styled from 'styled-components';

import { getPurpurColor } from '@telialt/common-theme/src/purpurSelectors';

type Props = Omit<Extract<CtaLinkProps, { href: string }>, 'iconOnly' | '[data-test]'> & {
    dataTest: string;
    disabled?: boolean;
};

const CtaLinkStyled = styled(CtaLink)<Pick<CtaLinkProps, 'variant'> & { $disabled?: boolean }>`
    &:focus {
        text-decoration: none;
        outline: 0;
    }
    &:hover,
    &:focus {
        color: ${({ variant }) =>
            (variant === 'primary' && getPurpurColor('textInteractiveOnPrimary')) ||
            (variant === 'secondary' && getPurpurColor('textInteractivePrimary')) ||
            (variant === 'expressive' && getPurpurColor('textInteractiveOnExpressive'))};
    }

    ${({ $disabled }) =>
        $disabled &&
        `
            pointer-events: none;
            background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
            color: ${getPurpurColor('textWeak')};
            
            &:focus {
                color: ${getPurpurColor('textWeak')};
                
                &:before {
                    display: none;
                }
            }
        `}
`;

export const ButtonLink: FC<Props> = ({
    children,
    dataTest,
    variant,
    disabled,
    onClick,
    showIcon = false,
    icon,
    ...props
}) => {
    return (
        <CtaLinkStyled
            data-test={dataTest}
            variant={variant}
            showIcon={showIcon}
            icon={icon}
            $disabled={disabled}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            {...props}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                if (disabled) {
                    return e.preventDefault();
                }

                onClick?.(e);
            }}
        >
            {children}
        </CtaLinkStyled>
    );
};
